import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { CapabilitySetting, FinanceApiService, FinancialCapability } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-capabilities',
  imports: [AinPageComponent, TableModule, RouterLink],
  providers: [MessageService],
  template: `
    <ain-page title="Finance Capabilities" description="Enable or disable tenant-level capabilities">
      <div class="toolbar">
        <a class="back-link" [routerLink]="['/financial/control-center']">Back to Control Center</a>
      </div>

      <p-table [value]="capabilities()">
        <ng-template pTemplate="header">
          <tr>
            <th>Key</th>
            <th>Core</th>
            <th>Dependencies</th>
            <th>Enabled</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.key }}</td>
            <td>{{ row.isCoreForBp2 ? 'Yes' : 'No' }}</td>
            <td>{{ row.dependencies.join(', ') || '-' }}</td>
            <td>
              <div class="radio-row">
                <label>
                  <input
                    type="radio"
                    [name]="'capability-' + row.key"
                    [checked]="isEnabled(row)"
                    [disabled]="row.isCoreForBp2 || isUpdating()[row.key]"
                    (change)="setCapability(row.key, true)" />
                  Enabled
                </label>
                <label>
                  <input
                    type="radio"
                    [name]="'capability-' + row.key"
                    [checked]="!isEnabled(row)"
                    [disabled]="row.isCoreForBp2 || isUpdating()[row.key]"
                    (change)="setCapability(row.key, false)" />
                  Disabled
                </label>
                @if (row.isCoreForBp2) {
                  <span class="hint">Core capability</span>
                }
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .radio-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
    .hint { color: #64748b; font-size: 0.85rem; }
  `]
})
export class FinanceControlCenterCapabilitiesComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly messages = inject(MessageService);

  readonly capabilities = signal<FinancialCapability[]>([]);
  readonly settings = signal<Record<string, CapabilitySetting>>({});
  readonly isUpdating = signal<Record<string, boolean>>({});

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.api.listCapabilities().subscribe({
      next: rows => {
        this.capabilities.set(rows);
        rows.forEach(row => {
          this.api.getCapabilitySetting(row.key).subscribe({
            next: setting => {
              this.settings.update(current => ({ ...current, [row.key]: setting }));
            }
          });
        });
      },
      error: () => this.messages.add({ severity: 'error', summary: 'Capabilities load failed' })
    });
  }

  isEnabled(capability: FinancialCapability): boolean {
    const setting = this.settings()[capability.key];
    if (setting) {
      return setting.isEnabled;
    }

    return capability.isCoreForBp2;
  }

  setCapability(capabilityKey: string, enabled: boolean): void {
    this.isUpdating.update(current => ({ ...current, [capabilityKey]: true }));
    this.api.setCapability(capabilityKey, enabled).subscribe({
      next: setting => {
        this.settings.update(current => ({ ...current, [setting.capabilityKey]: setting }));
        this.isUpdating.update(current => ({ ...current, [capabilityKey]: false }));
      },
      error: () => {
        this.isUpdating.update(current => ({ ...current, [capabilityKey]: false }));
        this.messages.add({ severity: 'error', summary: 'Capability update failed' });
      }
    });
  }
}
